import { Leads, User } from '.';
import _has from 'lodash-es/has';
import { action, computed, extendObservable, observable, reaction } from 'mobx';
import { app, browserHistory } from './../App';
import localForage = require('localforage');
import {
  ILeadNote,
  ILeadNewNote,
  ILeadEditNote,
  ILeadNoteVisibility,
  ILeadNoteUser,
  ILeadNotesInfo,
  ILeadNotesSubmitInfo,
  ILeadNotesUpdateInfo,
  IProviderDetails,
  IEditedProviderDetails,
  IBusinessType,
  IDrugLicense,
  IProviderShort,
  IProviderGroup,
} from 'common/interfaces/provider';

// Store
export class Lead {
  constructor(private leads: Leads, private user: User) {
    this.setQuestionnaireVisibility();
  }

  // React to user logout
  private reactToUserChange = reaction(
    () => this.user.id,
    (id: number) => {
      this.openLeads = [];
    }
  );

  // React to open lead change
  private reactToOpenLeadChange = reaction(
    () => this.openLeadId,
    (id: number) => {
      this.openLeadEditingDetails = false;
    }
  );

  // Questionnaire visibility
  public QUESTIONNAIRE_VISIBLE_STORAGE_KEY = 'questionnaire-visible';
  @observable public isQuestionnaireVisible: boolean = false;

  // Business types
  @observable public businessTypes: IBusinessType[] = [];
  @computed public get businessTypesLength(): number {
    return this.businessTypes.length;
  }

  // Drug license types
  @observable public drugLicenseTypes: IDrugLicense[] = [];
  @computed public get drugLicenseTypesLength(): number {
    return this.drugLicenseTypes.length;
  }

  // Details
  @observable public openLeadId: number | null = null;

  @observable public showRelationsOfOpenLead: boolean = false;

  @computed public get openLeadDetails(): IProviderDetails | null {
    if (this.openLeadId) {
      const leadDetailsIndex = this.leadsDetailsIds.indexOf(this.openLeadId);

      return leadDetailsIndex !== -1
        ? this.leadsDetails[leadDetailsIndex]
        : null;
    } else {
      return null;
    }
  }

  @computed public get openLeadGroup(): IProviderGroup | null {
    if (
      this.openLeadDetails !== null &&
      _has(this.openLeadDetails, 'providerGroup.id') &&
      _has(this.openLeadDetails, 'providerGroup.name')
    ) {
      return {
        id: this.openLeadDetails.providerGroup.id,
        name: this.openLeadDetails.providerGroup.name,
      };
    } else {
      return null;
    }
  }

  @computed public get leadTitle(): string | null {
    return this.openLeadDetails !== null &&
      (this.openLeadDetails.customerCreated || this.openLeadDetails.cardcode)
      ? 'Customer'
      : 'Lead';
  }

  @observable public disableOpenLeadShowModal: boolean = false;
  @observable public disablingOpenLead: boolean = false;
  @observable public disablingOpenLeadError: boolean = false;

  @observable public openLeadEditingDetails: boolean = false;
  @observable public openLeadEditingDetailsSaving: boolean = false;
  @observable public openLeadEditingDetailsSavingError: boolean = false;
  @observable public editedOpenLeadDetails: IEditedProviderDetails = {
    dba: '',
    legalBusName: '',
    providerId: '',
    salesReps: [],
    providerTypeCode: null,
    providerTypeName: null,
    dispenserClassCode: null,
    dispenserClassName: null,
    typeOfBusinessId: 0,
    typeOfBusinessName: '',
    drugLicenseTypeId: 0,
    drugLicenseTypeName: '',
    plAddress1: '',
    plAddress2: '',
    plCity: '',
    plState: '',
    plZipcode: '',
    plPhone: '',
    plExt: '',
    plFax: '',
    plEmail: '',
    modified: false,
  };

  @computed public get openLeadAddress(): string {
    let addr = '';

    if (this.openLeadDetails !== null) {
      const {
        plAddress1,
        plAddress2,
        plCity,
        plState,
        plZipcode,
      } = this.openLeadDetails;
      addr += plAddress1 ? ' ' + this.openLeadDetails.plAddress1 + ', ' : '';
      addr += plAddress2 ? ' ' + this.openLeadDetails.plAddress2 + ', ' : '';
      addr += plCity ? ' ' + this.openLeadDetails.plCity + ',' : '';
      addr += plState ? ' ' + this.openLeadDetails.plState : '';
      addr += plZipcode ? ' ' + this.openLeadDetails.plZipcode : '';
    }

    return addr.replace(/\s\s+/g, ' ');
  }

  @observable public openLeads: IProviderShort[] = [];
  @computed public get openLeadsIds(): number[] {
    return this.openLeads.map((lead) => lead.databaseId);
  }

  @observable public leadsDetails: IProviderDetails[] = [];
  @computed public get leadsDetailsIds(): number[] {
    return this.leadsDetails.map((lead) => lead.databaseId);
  }

  // Notes
  @computed public get openLeadNotes(): ILeadNote[] | null {
    if (this.openLeadId) {
      const leadDetailsIndex = this.leadsDetailsIds.indexOf(this.openLeadId);
      return leadDetailsIndex !== -1
        ? this.leadsDetails[leadDetailsIndex].notes
        : null;
    } else {
      return null;
    }
  }

  @observable public newNote: ILeadNewNote = {
    isSaving: false,
    error: false,
    errorAllFieldsRequired: false,
    html: '',
    text: '',
  };

  @observable public leadEditingNote: ILeadEditNote = {
    id: null,
    isSaving: false,
    error: false,
    html: '',
    text: '',
  };

  @observable public leadHidingNote: ILeadNoteVisibility = {
    id: null,
    isSaving: false,
    error: false,
  };

  // Fetch single lead
  @action public fetchLeadDetails = async (
    id: number
  ): Promise<IProviderDetails> => {
    try {
      // Fetch data from API
      const provider = await app.service('provider');
      const leadDetails: IProviderDetails = await provider.get(id);
      leadDetails.notes = observable.array([]);

      // Check if lead should only be updated
      const leadDetailsIndex = this.leadsDetailsIds.indexOf(id);
      if (leadDetailsIndex !== -1) {
        this.leadsDetails[leadDetailsIndex] = leadDetails;
      } else {
        this.leadsDetails.push(leadDetails);
      }

      // Return success
      return leadDetails;
    } catch (err) {
      // Display error
      console.error('Could not fetch lead of ID: ' + id, err);
      return err;
    }
  };

  // Open single lead
  @action public handleLeadLoad = async (id: number): Promise<any> => {
    try {
      // Prepare data
      let lead: IProviderShort | null = null;
      let leadDetails: IProviderDetails | null = null;

      // Load lead details if there aren't in state yet
      const leadDetailsIndex: number = this.leadsDetailsIds.indexOf(id);
      if (leadDetailsIndex === -1) {
        leadDetails = await this.fetchLeadDetails(id);
        await this.fetchLeadNotes(id);
      } else {
        leadDetails = this.leadsDetails[leadDetailsIndex];
      }

      // Create lead short object
      lead = {
        databaseId: leadDetails.databaseId,
        providerId: leadDetails.providerId,
        legalBusName: leadDetails.legalBusName,
        dba: leadDetails.dba,
      };

      // Add to open leads
      const openLeadIndex = this.openLeadsIds.indexOf(lead.databaseId);
      if (openLeadIndex === -1) {
        this.openLeads.push(lead);
      }

      // Mark as currently open lead
      this.openLeadId = lead.databaseId;

      return lead;
    } catch (err) {
      console.error(err);
      return err;
    }
  };

  // Disable single lead
  @action public handleOpenLeadDisable = (): void => {
    this.openLeadId = null;
  };

  // Select lead
  @action public handleLeadSelect = (id: number): void => {
    browserHistory.push('/sales/lead/' + id);
  };

  /* Generate lead card ID */
  public generateDashboardLeadCardId = (id: number): string => {
    return 'dashboard-lead-card-' + id;
  };

  /* Generate lead card name ID */
  public generateDashboardLeadCardNameId = (id: number): string => {
    return this.generateDashboardLeadCardId(id) + '-name';
  };

  /* Generate note card ID */
  public generateDashboardNoteCardId = (id: number): string => {
    return 'dashboard-note-card-' + id;
  };

  /* Generate note card name ID */
  public generateDashboardNoteCardNameId = (id: number): string => {
    return this.generateDashboardNoteCardId(id) + '-name';
  };

  // Close lead
  @action public handleLeadClose = (id: number): void => {
    let foundIndex = null;
    this.openLeads.some((openLead, i) => {
      if (openLead.databaseId === id) {
        foundIndex = i;
        return true;
      }
    });

    if (foundIndex !== null) {
      this.openLeads = this.openLeads
        .slice(0, foundIndex)
        .concat(this.openLeads.slice(foundIndex + 1));
    }

    // Navigate to sales dashboard
    if (this.openLeadId === id) {
      this.openLeadId = null;
      browserHistory.push('/sales');
    }
  };

  // Start editing lead details
  @action public startEditingOpenLeadDetails = () => {
    const providerType = this.leads.returnProviderTypeOrDispenserClass(
      this.openLeadDetails.type || null,
      'type'
    );
    const providerDispenserClass = this.leads.returnProviderTypeOrDispenserClass(
      this.openLeadDetails.dispenserClass || null,
      'dispenser'
    );

    this.openLeadEditingDetails = true;
    this.openLeadEditingDetailsSaving = false;
    this.openLeadEditingDetailsSavingError = false;
    this.editedOpenLeadDetails = {
      dba: this.openLeadDetails.dba || '',
      providerId: this.openLeadDetails.providerId || '',
      legalBusName: this.openLeadDetails.legalBusName || '',
      salesReps: this.openLeadDetails.salesReps || [],
      providerTypeCode: providerType ? providerType.code : null,
      providerTypeName: providerType ? providerType.name : null,
      dispenserClassCode: providerDispenserClass
        ? providerDispenserClass.code
        : null,
      dispenserClassName: providerDispenserClass
        ? providerDispenserClass.name
        : null,
      typeOfBusinessId:
        this.openLeadDetails.typeOfBusinessData &&
        this.openLeadDetails.typeOfBusinessData.id
          ? this.openLeadDetails.typeOfBusinessData.id
          : 0,
      typeOfBusinessName:
        this.openLeadDetails.typeOfBusinessData &&
        this.openLeadDetails.typeOfBusinessData.name
          ? this.openLeadDetails.typeOfBusinessData.name
          : '',
      drugLicenseTypeId:
        this.openLeadDetails.typeOfDrugLicenseData &&
        this.openLeadDetails.typeOfDrugLicenseData.id
          ? this.openLeadDetails.typeOfDrugLicenseData.id
          : 0,
      drugLicenseTypeName:
        this.openLeadDetails.typeOfDrugLicenseData &&
        this.openLeadDetails.typeOfDrugLicenseData.name
          ? this.openLeadDetails.typeOfDrugLicenseData.name
          : '',
      plAddress1: this.openLeadDetails.plAddress1 || '',
      plAddress2: this.openLeadDetails.plAddress2 || '',
      plCity: this.openLeadDetails.plCity || '',
      plState: this.openLeadDetails.plState || '',
      plZipcode: this.openLeadDetails.plZipcode
        ? this.openLeadDetails.plZipcode.toString()
        : '',
      plPhone: this.openLeadDetails.plPhone || '',
      plExt: this.openLeadDetails.plExt || '',
      plFax: this.openLeadDetails.plFax || '',
      plEmail: this.openLeadDetails.plEmail || '',
      modified: false,
    };
  };

  // Stop editing lead details
  @action public stopEditingOpenLeadDetails = () => {
    this.openLeadEditingDetails = false;
    this.openLeadEditingDetailsSaving = false;
    this.openLeadEditingDetailsSavingError = false;
    this.editedOpenLeadDetails = {
      dba: '',
      providerId: '',
      legalBusName: '',
      salesReps: [],
      providerTypeCode: null,
      providerTypeName: null,
      dispenserClassCode: null,
      dispenserClassName: null,
      typeOfBusinessId: 0,
      typeOfBusinessName: '',
      drugLicenseTypeId: 0,
      drugLicenseTypeName: '',
      plAddress1: '',
      plAddress2: '',
      plCity: '',
      plState: '',
      plZipcode: '',
      plPhone: '',
      plExt: '',
      plFax: '',
      plEmail: '',
      modified: false,
    };
  };

  // Save
  @action public saveUpdatedLeadDetails = async (): Promise<any> => {
    try {
      this.openLeadEditingDetails = false;
      this.openLeadEditingDetailsSaving = true;
      this.openLeadEditingDetailsSavingError = false;

      if (this.editedOpenLeadDetails.modified) {
        let zipcode: number | null = parseInt(
          this.editedOpenLeadDetails.plZipcode.trim(),
          10
        );
        zipcode = isNaN(zipcode) ? null : zipcode;

        const provider = await app.service('provider');
        const patch: IProviderDetails = await provider.patch(this.openLeadId, {
          providerId: this.editedOpenLeadDetails.providerId.trim(),
          dba: this.editedOpenLeadDetails.dba.trim(),
          legalBusName: this.editedOpenLeadDetails.legalBusName.trim(),
          type: this.editedOpenLeadDetails.providerTypeCode || null,
          dispenserClass: this.editedOpenLeadDetails.dispenserClassCode || null,
          typeOfBusiness: this.editedOpenLeadDetails.typeOfBusinessId || null,
          drugLicenseType: this.editedOpenLeadDetails.drugLicenseTypeId || null,
          plCity: this.editedOpenLeadDetails.plCity.trim() || null,
          plAddress1: this.editedOpenLeadDetails.plAddress1.trim() || null,
          plAddress2: this.editedOpenLeadDetails.plAddress2.trim() || null,
          plState: this.editedOpenLeadDetails.plState.trim() || null,
          plZipcode: zipcode,
          plPhone: this.editedOpenLeadDetails.plPhone.trim() || null,
          plExt: this.editedOpenLeadDetails.plExt.trim() || null,
          plFax: this.editedOpenLeadDetails.plFax.trim() || null,
          plEmail: this.editedOpenLeadDetails.plEmail.trim() || null,
        });

        const providerSalesReps = await app.service('provider-to-salesrep');
        const repsPatch = await providerSalesReps.create({
          providerId: this.openLeadId,
          salesReps: this.editedOpenLeadDetails.salesReps.map((rep) => ({
            slpcode: rep.slpcode,
            primary: rep.primary ? 1 : 0,
          })),
        });

        const leadDetailsIndex = this.leadsDetailsIds.indexOf(this.openLeadId);
        this.leadsDetails[
          leadDetailsIndex
        ].salesReps = this.editedOpenLeadDetails.salesReps;
        this.leadsDetails[leadDetailsIndex].dba = patch.dba;
        this.leadsDetails[leadDetailsIndex].providerId = patch.providerId;
        this.leadsDetails[leadDetailsIndex].legalBusName = patch.legalBusName;
        this.leadsDetails[leadDetailsIndex].type = patch.type;
        this.leadsDetails[leadDetailsIndex].dispenserClass =
          patch.dispenserClass;
        this.leadsDetails[
          leadDetailsIndex
        ].typeOfBusinessData = patch.typeOfBusinessData
          ? {
              id: patch.typeOfBusinessData.id,
              name: patch.typeOfBusinessData.name,
            }
          : null;
        this.leadsDetails[
          leadDetailsIndex
        ].typeOfDrugLicenseData = patch.typeOfDrugLicenseData
          ? {
              id: patch.typeOfDrugLicenseData.id,
              name: patch.typeOfDrugLicenseData.name,
            }
          : null;
        this.leadsDetails[leadDetailsIndex].plCity = patch.plCity;
        this.leadsDetails[leadDetailsIndex].plAddress1 = patch.plAddress1;
        this.leadsDetails[leadDetailsIndex].plAddress2 = patch.plAddress2;
        this.leadsDetails[leadDetailsIndex].plState = patch.plState;
        this.leadsDetails[leadDetailsIndex].plZipcode = patch.plZipcode;
        this.leadsDetails[leadDetailsIndex].plPhone = patch.plPhone;
        this.leadsDetails[leadDetailsIndex].plExt = patch.plExt;
        this.leadsDetails[leadDetailsIndex].plFax = patch.plFax;
        this.leadsDetails[leadDetailsIndex].plEmail = patch.plEmail;

        this.openLeadEditingDetails = false;
        this.openLeadEditingDetailsSaving = false;
        this.openLeadEditingDetailsSavingError = false;
      } else {
        this.openLeadEditingDetails = false;
        this.openLeadEditingDetailsSaving = false;
        this.openLeadEditingDetailsSavingError = false;
      }

      const result = this.editedOpenLeadDetails;
      this.editedOpenLeadDetails = {
        dba: '',
        providerId: '',
        legalBusName: '',
        salesReps: [],
        providerTypeCode: null,
        providerTypeName: null,
        dispenserClassCode: null,
        dispenserClassName: null,
        typeOfBusinessId: 0,
        typeOfBusinessName: '',
        drugLicenseTypeId: 0,
        drugLicenseTypeName: '',
        plAddress1: '',
        plAddress2: '',
        plCity: '',
        plState: '',
        plZipcode: '',
        plPhone: '',
        plExt: '',
        plFax: '',
        plEmail: '',
        modified: false,
      };
      return result;
    } catch (err) {
      this.openLeadEditingDetailsSaving = false;
      this.openLeadEditingDetailsSavingError = true;
      console.error(err);
      return err;
    }
  };

  // Fetch all notes from lead
  @action public fetchLeadNotes = async (
    leadId: number
  ): Promise<ILeadNotesInfo> => {
    try {
      const leadNotesDb: any = app.service('comment');
      const leadNotesInfo = (await leadNotesDb.find({
        query: {
          providerId: leadId,
          $limit: 10000,
          $sort: {
            updatedAt: -1,
          },
        },
      })) as ILeadNotesInfo;

      const leadDetailsIndex = this.leadsDetailsIds.indexOf(leadId);
      if (
        leadDetailsIndex !== -1 &&
        typeof this.leadsDetails[leadDetailsIndex].notes !== 'undefined'
      ) {
        leadNotesInfo.data.map((note) => {
          this.leadsDetails[leadDetailsIndex].notes.push(note);
        });
      } else if (
        leadDetailsIndex !== -1 &&
        typeof this.leadsDetails[leadDetailsIndex].notes === 'undefined'
      ) {
        extendObservable(this.leadsDetails[leadDetailsIndex], {
          notes: leadNotesInfo,
        });
      }

      return leadNotesInfo;
    } catch (err) {
      console.error('Could not fetch lead notes for ID: ' + leadId, err);
      throw err;
    }
  };

  @action public addLeadNote = async (): Promise<any> => {
    if (this.newNote.text.trim().length <= 0) {
      return;
    } else {
      try {
        // Get ID
        const id = this.openLeadId;

        // Start saving
        this.newNote.isSaving = true;
        this.newNote.error = false;
        this.newNote.errorAllFieldsRequired = false;

        // Store note
        const note = this.newNote.text.trim();

        // Create note
        const newNoteCreated = await this.submitLeadNote({
          comment: note,
          providerId: id,
        });

        // Disable editing
        this.newNote = {
          isSaving: false,
          error: false,
          errorAllFieldsRequired: false,
          html: '',
          text: '',
        };

        // Append new note
        const leadNoteCreatedByUser: ILeadNoteUser = {
          id: this.user.id,
          username: this.user.username,
          name: this.user.name,
          email: this.user.email,
        };
        const leadDetailsIndex = this.leadsDetailsIds.indexOf(id);
        const { notes } = this.leadsDetails[leadDetailsIndex];
        this.leadsDetails[leadDetailsIndex].notes.unshift({
          id: newNoteCreated.id,
          comment: newNoteCreated.comment,
          hidden: 0,
          providerId: newNoteCreated.providerId,
          createdAt: newNoteCreated.createdAt,
          createdByUser: leadNoteCreatedByUser,
          updatedAt: newNoteCreated.updatedAt,
          updatedByUser: leadNoteCreatedByUser,
        });

        // Return boolean true if succesfull
        return true;
      } catch (err) {
        this.newNote = {
          ...this.newNote,
          isSaving: false,
          error: true,
        };
        console.error(err);
        throw err;
      }
    }
  };

  @action public clearNewLeadNote = () => {
    if (this.newNote.isSaving === false) {
      this.newNote.error = false;
      this.newNote.errorAllFieldsRequired = false;
      this.newNote.html = '';
      this.newNote.text = '';
    }
  };

  @action public submitLeadNote = async (
    leadNotesSubmitInfo: ILeadNotesSubmitInfo
  ): Promise<ILeadNote> => {
    try {
      const leadNotesDb: any = app.service('comment');
      const response: ILeadNote = await leadNotesDb.create(leadNotesSubmitInfo);
      return response;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  @action public fetchBusinessTypes = async (): Promise<IBusinessType[]> => {
    try {
      const service = app.service('type-of-business');
      const businessTypes: IBusinessType[] = (await service.find({
        query: {
          $sort: {
            name: 1,
          },
        },
      })) as IBusinessType[];
      this.businessTypes = businessTypes;
      return businessTypes;
    } catch (err) {
      console.error(err);
      return err;
    }
  };

  @action public fetchDrugLicenseTypes = async (): Promise<IDrugLicense[]> => {
    try {
      const service = app.service('drug-license-type');
      const drugLicenseTypes: IDrugLicense[] = await service.find({
        query: {
          $sort: {
            name: 1,
          },
        },
      });
      this.drugLicenseTypes = drugLicenseTypes;
      return drugLicenseTypes;
    } catch (err) {
      console.error(err);
      return err;
    }
  };

  @action public updateLeadNote = async (
    id: number,
    leadNotesUpdateInfo: ILeadNotesUpdateInfo
  ): Promise<any> => {
    try {
      const leadNotesDb: any = app.service('comment');
      const response = await leadNotesDb.patch(id, leadNotesUpdateInfo);
      return response;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  @action public toggleQuestionnaireVisibility = () => {
    const willBeVisible = !this.isQuestionnaireVisible;

    this.isQuestionnaireVisible = willBeVisible;
    localForage.setItem(this.QUESTIONNAIRE_VISIBLE_STORAGE_KEY, willBeVisible);
  };

  @action public openDisableLeadModal = () => {
    this.disableOpenLeadShowModal = true;
  };

  @action public closeDisableLeadModal = () => {
    this.disableOpenLeadShowModal = false;
  };

  @action public disableLead = async (databaseId: number): Promise<boolean> => {
    try {
      this.disablingOpenLead = true;
      this.disablingOpenLeadError = false;

      const service = await app.service('provider');
      const patch = await service.patch(databaseId, {
        active: false,
      });

      this.disableOpenLeadShowModal = false;

      let disabledLeadIndex: number = -1;
      this.openLeads.some((lead, leadIndex) => {
        if (lead.databaseId === databaseId) {
          disabledLeadIndex = leadIndex;
          return true;
        }
      });

      if (disabledLeadIndex !== -1) {
        this.openLeads.splice(disabledLeadIndex);
      }

      browserHistory.push('/sales');

      this.leads.allLeads = [];
      this.leads.leadsToDisplayByQuery.clear();
      this.leads.leadsSearch = '';
      this.leads.fetchLeads();

      return true;
    } catch (err) {
      this.disablingOpenLeadError = true;
      console.error(err);
      return false;
    } finally {
      this.disablingOpenLead = false;
    }
  };

  private setQuestionnaireVisibility = () => {
    localForage
      .getItem<boolean>(this.QUESTIONNAIRE_VISIBLE_STORAGE_KEY)
      .then((isVisible) => {
        if (isVisible) {
          this.isQuestionnaireVisible = isVisible;
        }
      });
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/stores/Lead.ts